import React, { useMemo } from 'react';

import { useAppSelector } from '../../../../hooks/redux';
import { AmhsInteractiveGroup } from '../shared';

import {
  pickEffectiveDetail,
  useAmhsDetailLevel,
} from '../rendering';

import { AmhsInstancedBatch } from '../rendering';

import {
  kitFoupProxyDimensions,
  resolveStockerRackFoupScale,
  resolveStockerRackFoupY,
} from '../types';

import { StockerLoadPortBank } from './StockerLoadPortBank';

import { StockerBody } from './StockerBody';

import { StockerGlass } from './StockerGlass';

import { StockerInterior } from './StockerInterior';

import { StockerTray } from './StockerTray';
import * as THREE from 'three';

import { StockerRobot } from './StockerRobot';
import { useStockerTransferRobotState } from './useStockerTransferRobotState';
import { useStockerRackDisplayCount } from './useStockerRackDisplayCount';
import { useStockerInventoryVisuals } from './useStockerInventoryVisuals';

import { StockerLights } from './StockerLights';

import { getStockerMaterials } from './StockerMaterials';

import {
  buildStockerRackLayout,
  buildStockerTransferSlotPositions,
  rackLayoutToFoupSlotStates,
} from './stockerRackLayout';
import { StockerTransferAnim } from './StockerTransferAnim';
import { StockerRackFrame } from './StockerRackFrame';
import {
  FAB_STOCKER_DEFAULTS,
  computeFrontLoadPortPositions,
  resolveOhtDropPosition,
  resolveStockerDimensions,
  type Stocker3DProps,
} from './stockerTypes';



export type { Stocker3DProps, StockerRobotState, StockerAmhsEvent } from './stockerTypes';

export {
  FAB_STOCKER_DEFAULTS,
  computeFrontLoadPortPositions,
  computeStockerSlotLayout,
  resolveOhtDropPosition,
} from './stockerTypes';
export { buildStockerRackLayout, rackLayoutToFoupSlotStates } from './stockerRackLayout';

const stockerSignTextureCache = new Map<string, THREE.CanvasTexture>();

function getStockerSignTexture(
  stockerTitle: string,
  effectiveRackCount: number,
  totalSlots: number,
  status: string
): THREE.CanvasTexture {
  const cacheKey = `${stockerTitle}|${effectiveRackCount}|${totalSlots}|${status}`;
  let tex = stockerSignTextureCache.get(cacheKey);
  if (!tex) {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 256;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(0.5, 0.5);
      // Dark Glossy Obsidian Faceplate Background
      ctx.fillStyle = '#060C18';
      ctx.fillRect(0, 0, 1024, 512);

      // Glowing Cyan Border Lip
      ctx.strokeStyle = '#38BDF8';
      ctx.lineWidth = 14;
      ctx.strokeRect(10, 10, 1004, 492);

      // Inner Metallic Frame
      ctx.strokeStyle = '#334155';
      ctx.lineWidth = 4;
      ctx.strokeRect(26, 26, 972, 460);

      // Top Status Pill
      const isErr = status === 'error';
      ctx.fillStyle = isErr ? '#DC2626' : '#10B981';
      ctx.beginPath();
      if (typeof (ctx as any).roundRect === 'function') {
        (ctx as any).roundRect(44, 44, 210, 80, 20);
      } else {
        ctx.rect(44, 44, 210, 80);
      }
      ctx.fill();

      ctx.fillStyle = isErr ? '#FEF2F2' : '#022C22';
      ctx.font = 'bold 42px "Segoe UI", Inter, Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(isErr ? '● ERR' : '● STK', 149, 84);

      // Top Stocker Name
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 52px "Segoe UI", Inter, Arial, sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(stockerTitle, 286, 84);

      // Middle Glowing Seam Line
      ctx.fillStyle = '#00F0FF';
      ctx.fillRect(44, 160, 936, 6);

      // Bottom Big Count Display
      ctx.font = '900 185px "Segoe UI", Inter, Arial, sans-serif';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#00F0FF';
      ctx.fillText(`${effectiveRackCount}`, 510, 335);

      ctx.textAlign = 'left';
      ctx.fillStyle = '#FFFFFF';
      ctx.fillText(`/${totalSlots}`, 525, 335);
    }
    tex = new THREE.CanvasTexture(canvas);
    tex.anisotropy = 8;
    tex.needsUpdate = true;
    stockerSignTextureCache.set(cacheKey, tex);
  }
  return tex;
}

const StockerSignBadge: React.FC<{
  id: string;
  label?: string;
  totalSlots: number;
  effectiveRackCount: number;
  rawCount?: number;
  processedCount?: number;
  status?: string;
  position: [number, number, number];
  rotation?: [number, number, number];
  plateWidth?: number;
  plateHeight?: number;
}> = ({
  id,
  label,
  totalSlots,
  effectiveRackCount,
  status = 'ready',
  position,
  rotation = [0, 0, 0],
  plateWidth = 6.4,
  plateHeight = 3.2,
}) => {
  const stockerTitle = (label?.trim() || id?.trim() || 'STOCKER1').toUpperCase();
  const badgeTexture = useMemo(
    () => getStockerSignTexture(stockerTitle, effectiveRackCount, totalSlots, status),
    [stockerTitle, effectiveRackCount, totalSlots, status]
  );

  return (
    <group position={position} rotation={rotation}>
      {/* Solid Rear Housing */}
      <mesh position={[0, 0, 0.02]} castShadow>
        <boxGeometry args={[plateWidth + 0.16, plateHeight + 0.16, 0.05]} />
        <meshStandardMaterial color="#0A101D" metalness={0.88} roughness={0.25} />
      </mesh>
      {/* Outer Cyan Glow Rim */}
      <mesh position={[0, 0, 0.02]}>
        <boxGeometry args={[plateWidth + 0.24, plateHeight + 0.24, 0.04]} />
        <meshStandardMaterial
          color="#38BDF8"
          emissive="#00F0FF"
          emissiveIntensity={0.9}
          toneMapped={false}
        />
      </mesh>
      {/* Crisp 2D Canvas Badge Texture Faceplate (Front) */}
      <mesh position={[0, 0, 0.055]} renderOrder={10}>
        <planeGeometry args={[plateWidth, plateHeight]} />
        <meshBasicMaterial
          map={badgeTexture}
          transparent
          side={THREE.DoubleSide}
          depthWrite={false}
          toneMapped={false}
        />
      </mesh>
      {/* Crisp 2D Canvas Badge Texture Faceplate (Back) */}
      <mesh position={[0, 0, -0.015]} rotation={[0, Math.PI, 0]} renderOrder={10}>
        <planeGeometry args={[plateWidth, plateHeight]} />
        <meshBasicMaterial
          map={badgeTexture}
          transparent
          side={THREE.DoubleSide}
          depthWrite={false}
          toneMapped={false}
        />
      </mesh>
    </group>
  );
};

/**
 * Reference-matched semiconductor FAB stocker — dual rack columns, central robot,
 * full-height glass, white top cap, floating inventory labels on right jamb.
 */
export const Stocker3D: React.FC<Stocker3DProps> = ({

  id = 'STK-01',

  label,

  width = FAB_STOCKER_DEFAULTS.width,

  depth = FAB_STOCKER_DEFAULTS.depth,

  height = FAB_STOCKER_DEFAULTS.height,

  loadPortCount = FAB_STOCKER_DEFAULTS.loadPortCount,

  occupiedCount,

  status = 'ready',

  detail: detailProp,

  autoDetail = false,

  opacity = 1,

  animate: _animate = false,

  position = [0, 0, 0],

  rotation = [0, 0, 0],

  onSelect,

  bayCount,

  outsideStocker = false,

  layoutPorts,

  robotState: robotStateProp,

  loadPorts: _loadPorts,

  slots: slotsOverride,

  ohtDropHeight = 1.2,

  onFoupReceived: _onFoupReceived,

  onFoupPicked: _onFoupPicked,

  onFoupStored: _onFoupStored,

  onFoupRetrieved: _onFoupRetrieved,

  onFoupPlaced: _onFoupPlaced,

  onAmhsEvent: _onAmhsEvent,

}) => {

  void _loadPorts;

  void _onFoupReceived;

  void _onFoupPicked;

  void _onFoupStored;

  void _onFoupRetrieved;

  void _onFoupPlaced;

  void _onAmhsEvent;
  void _animate;

  const robotState = useStockerTransferRobotState(id, robotStateProp);
  const simulationRunning = useAppSelector((s) => s.simulation.running);
  const stockerPortOwners = useAppSelector((s) => s.simulation.browserKinetic?.stockerPortOwners);
  const stationAliases = useMemo(() => {
    const aliases = new Set<string>();
    if (label) aliases.add(label);
    for (const port of layoutPorts ?? []) {
      if (typeof port?.id === 'string' && port.id.length > 0) {
        const ownerId = stockerPortOwners?.[port.id];
        if (ownerId) aliases.add(ownerId);
      }
    }
    return [...aliases];
  }, [label, layoutPorts, stockerPortOwners]);

  const autoTier = useAmhsDetailLevel(position, autoDetail);

  const detail = pickEffectiveDetail(detailProp, autoTier, autoDetail);

  const dims = useMemo(() => resolveStockerDimensions(width, height, depth), [width, height, depth]);

  const rackLayout = useMemo(
    () =>
      buildStockerRackLayout(width, height, depth, bayCount, {
        bottom: dims.rackBottom,
        top: dims.rackTop,
      }),
    [width, height, depth, bayCount, dims.rackBottom, dims.rackTop]
  );

  const trayW = rackLayout.slotW;
  const trayD = rackLayout.slotD;
  const trayH = rackLayout.slotH * 0.12;
  const tierGap = useMemo(() => {
    const ys = [...new Set(rackLayout.slots.map((s) => s.position[1]))].sort((a, b) => a - b);
    return ys.length >= 2 ? ys[1]! - ys[0]! - rackLayout.slotH : rackLayout.slotH * 0.14;
  }, [rackLayout]);
  const totalSlots = rackLayout.totalSlots;

  const rackDisplayCount = useStockerRackDisplayCount(
    id,
    occupiedCount,
    totalSlots,
    0.82,
    stationAliases
  );

  const inventoryVisuals = useStockerInventoryVisuals(id, rackDisplayCount);
  const effectiveRackCount = Math.min(totalSlots, rackDisplayCount);

  const slotLayout = useMemo(() => {
    const base =
      slotsOverride ?? rackLayoutToFoupSlotStates(rackLayout, trayH);
    const fillRatio = effectiveRackCount / Math.max(1, base.length);
    return base.map((slot, i) => {
      const kind = inventoryVisuals.slotKinds.get(i);
      const occupied =
        slotsOverride != null ? Boolean(slot.occupied) : i < effectiveRackCount;
      return {
        ...slot,
        occupied,
        highlighted: i === Math.floor(base.length * fillRatio * 0.5),
        status: kind ?? (slot.status ?? 'raw'),
      };
    });
  }, [slotsOverride, rackLayout, trayH, effectiveRackCount, inventoryVisuals.slotKinds]);



  const frontPortPositions = useMemo(

    () => computeFrontLoadPortPositions(width, height, depth, loadPortCount),

    [width, height, depth, loadPortCount]

  );



  const ohtDropPosition = useMemo(

    () =>

      resolveOhtDropPosition(

        frontPortPositions[1] ?? frontPortPositions[0] ?? [0, height * 0.1, depth * 0.46],

        height,

        ohtDropHeight

      ),

    [frontPortPositions, height, ohtDropHeight]

  );



  const foupScale = useMemo(
    () => resolveStockerRackFoupScale(trayW, trayD, rackLayout.slotH, trayH),
    [trayW, trayD, trayH, rackLayout.slotH]
  );

  const foupProxy = useMemo(() => kitFoupProxyDimensions(foupScale), [foupScale]);

  const mats = useMemo(() => getStockerMaterials(opacity), [opacity]);



  const transferSlotPositions = useMemo(
    () => buildStockerTransferSlotPositions(width, height, depth, bayCount),
    [width, height, depth, bayCount]
  );

  const storedFoupsCount = occupiedCount ?? 0;

  const instancedFoups = useMemo(() => {
    const raw: typeof slotLayout = [];
    const processed: typeof slotLayout = [];
    for (const slot of slotLayout) {
      if (!slot.occupied) continue;
      if (slot.status === 'processed') processed.push(slot);
      else raw.push(slot);
    }
    const toInstances = (slots: typeof slotLayout) =>
      slots.map((s) => ({
        position: [
          s.position[0],
          resolveStockerRackFoupY(s.position[1], trayH, foupProxy.height, 'instanced'),
          s.position[2],
        ] as [number, number, number],
        scale: [foupProxy.width, foupProxy.height, foupProxy.depth] as [
          number,
          number,
          number,
        ],
      }));
    return {
      raw: toInstances(raw),
      processed: toInstances(processed),
    };
  }, [slotLayout, foupProxy, trayH]);



  const showGlass = true;
  const showRobot = true;
  const showTrays = true;
  const showFoups = true;



  const cabD = Math.min(width * 0.28, Math.max(depth * 0.4, 1.75));
  const nestD = Math.max(cabD * 0.95, 0.95);
  const totalD = nestD + 0.4;
  const smallGap = 0.18;
  const stockerBodyZOffset = dims.loadPortZ + totalD + smallGap - dims.frontFaceZ;

  return (

    <AmhsInteractiveGroup name={`Stocker3D-${id}`} position={position} rotation={rotation} onSelect={onSelect}>

      <group name="Stocker3D">

        {/* Solid Industrial Connecting Base Deck between Load Port and Stocker Body */}
        <mesh
          position={[0, dims.baseH * 0.5, (dims.loadPortZ + (-depth * 0.46 + stockerBodyZOffset)) * 0.5]}
          material={mats.darkMetal}
          receiveShadow
        >
          <boxGeometry args={[width * 0.88, dims.baseH, Math.max(0.2, Math.abs((-depth * 0.46 + stockerBodyZOffset) - dims.loadPortZ))]} />
        </mesh>

        {/* ── STOCKER MAIN CABINET (SHIFTED SNUGLY BEHIND LIFTER WITH SMALL GAP) ── */}
        <group position={[0, 0, stockerBodyZOffset]}>
          <StockerBody dims={dims} opacity={opacity} />

          <StockerInterior dims={dims} opacity={opacity} />

          {showTrays ? (
            <StockerRackFrame dims={dims} rack={rackLayout} opacity={opacity} />
          ) : null}

          {showTrays ? (
            <StockerTray

              slots={slotLayout}

              trayW={trayW}

              trayD={trayD}

              trayH={trayH}
              slotH={rackLayout.slotH}
              tierGap={tierGap}

              opacity={opacity}

              detail={detail}

            />

          ) : null}



          {showFoups && (
            <>
              {instancedFoups.raw.length > 0 ? (
                <AmhsInstancedBatch instances={instancedFoups.raw} material={mats.foupShell} />
              ) : null}
              {instancedFoups.processed.length > 0 ? (
                <AmhsInstancedBatch
                  instances={instancedFoups.processed}
                  material={mats.foupProcessedShell}
                />
              ) : null}
            </>
          )}



          {showRobot ? (

            <StockerRobot

              dims={dims}

              elementId={id}

              robotState={robotState}

              opacity={opacity}

              foupScale={foupScale}

            />

          ) : null}



          <StockerLights dims={dims} status={status} opacity={opacity} />

          {/* Upper glass only — stops at load-port lintel */}
          {showGlass ? <StockerGlass dims={dims} detail={detail} opacity={opacity} /> : null}

          {/* ── Stocker Identification & Count Badges on Left and Right Faces ── */}
          {/* Left Side Face Badge */}
          <StockerSignBadge
            id={id}
            label={label}
            totalSlots={totalSlots}
            effectiveRackCount={effectiveRackCount}
            rawCount={inventoryVisuals.rawCount}
            processedCount={inventoryVisuals.processedCount}
            status={status}
            position={[-width * 0.5 - 0.08, dims.baseH + (dims.pillarTop - dims.baseH) * 0.55, 0]}
            rotation={[0, -Math.PI / 2, 0]}
            plateWidth={Math.min(depth * 0.65, 8.5)}
            plateHeight={3.2}
          />

          {/* Right Side Face Badge */}
          <StockerSignBadge
            id={id}
            label={label}
            totalSlots={totalSlots}
            effectiveRackCount={effectiveRackCount}
            rawCount={inventoryVisuals.rawCount}
            processedCount={inventoryVisuals.processedCount}
            status={status}
            position={[width * 0.5 + 0.08, dims.baseH + (dims.pillarTop - dims.baseH) * 0.55, 0]}
            rotation={[0, Math.PI / 2, 0]}
            plateWidth={Math.min(depth * 0.65, 8.5)}
            plateHeight={3.2}
          />
        </group>

        {/* External load-port bay — protrudes in front of frame, connected to base */}
        <StockerLoadPortBank
          width={width}
          height={height}
          depth={depth}
          dims={dims}
          opacity={opacity}
          bayCount={bayCount}
          outsideStocker={outsideStocker}
          layoutPorts={layoutPorts}
          stockerId={id}
        />



        <object3D name="OhtDropAnchor" position={ohtDropPosition.toArray()} visible={false} />

        {simulationRunning && id ? (
          <StockerTransferAnim
            width={width}
            height={height}
            depth={depth}
            elementId={id}
            bayCount={bayCount}
            outsideStocker={outsideStocker}
            storedFoupsCount={storedFoupsCount}
            totalSlots={totalSlots}
            slotPositions={transferSlotPositions}
            foupScale={foupScale}
            opacity={opacity}
            showLoadPortBank={false}
            showCrane={false}
            driveRobot
          />
        ) : null}

      </group>

    </AmhsInteractiveGroup>

  );

};



export default Stocker3D;

